import React, { useContext } from 'react';
import { Box, Flex, Text, Button, VStack, Heading, Icon, Image, Container, HStack, Badge, Divider } from '@chakra-ui/react';
import { FaWindows, FaDownload, FaExclamationTriangle, FaShieldAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import AuthContext from '../services/AuthContext';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const DesktopUpdateGate = () => {
  const { logout, user } = useContext(AuthContext);

  const brandYellow = '#F2C919';
  const lightBg = 'white';
  const primaryText = 'black';
  const downloadUrl = 'https://api.cobra-education.com/uploads/apps/Cobra-Education-Setup.exe';

  return (
    <Box dir="rtl" bg={lightBg} minH="100vh" color={primaryText} overflowX="hidden">
      <Header />

      <Container maxW="1100px" py={{ base: 10, md: 16 }}>
        <VStack spacing={8} align="stretch">
          
          {/* Top Banner Alert */}
          <Box
            bg="red.50"
            border="2px solid"
            borderColor="red.300"
            borderRadius="2xl"
            p={{ base: 4, md: 6 }}
            boxShadow="sm"
          >
            <Flex direction={{ base: "column", md: "row" }} align="center" gap={4} textAlign={{ base: "center", md: "right" }}>
              <Flex
                w="60px"
                h="60px"
                bg="red.500"
                color="white"
                borderRadius="full"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Icon as={FaExclamationTriangle} boxSize={7} />
              </Flex>
              <Box flex={1}>
                <HStack spacing={2} justify={{ base: "center", md: "flex-start" }} mb={1}>
                  <Badge colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="full">
                    تحديث إجباري مطلوب
                  </Badge>
                  <Badge colorScheme="gray" fontSize="xs" px={2} py={0.5} borderRadius="full">
                    الإصدار 1.1.0
                  </Badge>
                </HStack>
                <Heading as="h3" size="md" color="red.800" fontFamily="Tajawal, sans-serif" mb={1}>
                  يرجى تحديث برنامج الحاسوب للاستمرار
                </Heading>
                <Text color="red.700" fontSize="sm" fontFamily="Tajawal, sans-serif">
                  الإصدار المثبت حالياً على جهازك قديم ولم يعد مدعوماً. لن تتمكن من دخول الحصص المباشرة أو استخدام المنصة حتى تثبيت الإصدار الجديد.
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* Main Card */}
          <Flex
            direction={{ base: "column", lg: "row" }}
            align="center"
            justify="space-between"
            bg="gray.50"
            borderRadius="3xl"
            p={{ base: 6, md: 12 }}
            border="1px solid"
            borderColor="gray.200"
            gap={10}
          >
            {/* Left Content */}
            <VStack align={{ base: "center", lg: "flex-start" }} spacing={6} flex={1} textAlign={{ base: "center", lg: "right" }}>
              <Box>
                <Text color={brandYellow} fontWeight="extrabold" fontSize="sm" letterSpacing="wider" textTransform="uppercase" mb={2}>
                  COBRA EDUCATION DESKTOP
                </Text>
                <Heading as="h2" fontSize={{ base: "3xl", md: "5xl" }} color="gray.900" fontFamily="Tajawal, sans-serif" fontWeight="black" lineHeight="1.2" mb={4}>
                  تحديث برنامج الحاسوب
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" lineHeight="1.8" fontFamily="Tajawal, sans-serif" maxW="520px">
                  يتضمن هذا التحديث تحسينات أمنية هامة وإصلاحات لمزامنة الصوت وإدارة الميكروفون في الحصص المباشرة. التحديث مجاني وسريع ولا يستغرق سوى دقائق معدودة.
                </Text>
              </Box>

              {/* Steps */}
              <VStack align="flex-start" spacing={3} w="100%" maxW="480px" bg="white" p={5} borderRadius="xl" border="1px solid" borderColor="gray.200">
                <Text fontWeight="bold" fontSize="sm" color="gray.700" fontFamily="Tajawal, sans-serif">
                  خطوات التحديث السريعة:
                </Text>
                <HStack align="flex-start" spacing={3}>
                  <Badge colorScheme="blue" borderRadius="full" px={2}>1</Badge>
                  <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                    اضغط على زر <b>تحميل التحديث الآن</b> في الأسفل.
                  </Text>
                </HStack>
                <HStack align="flex-start" spacing={3}>
                  <Badge colorScheme="blue" borderRadius="full" px={2}>2</Badge>
                  <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                    بعد اكتمال التحميل، افتح ملف <b>Cobra-Education-Setup.exe</b>.
                  </Text>
                </HStack>
                <HStack align="flex-start" spacing={3}>
                  <Badge colorScheme="blue" borderRadius="full" px={2}>3</Badge>
                  <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                    سيتم تثبيت التحديث وفتح البرنامج تلقائياً بالإصدار الجديد.
                  </Text>
                </HStack>
              </VStack>

              {/* Action Buttons */}
              <VStack spacing={4} w="100%" maxW="380px" pt={2}>
                <Button
                  as="a"
                  href={downloadUrl}
                  download="Cobra-Education-Setup.exe"
                  size="lg"
                  h="65px"
                  w="100%"
                  bg="black"
                  color="white"
                  rightIcon={<Icon as={FaDownload} boxSize={6} ml={3} />}
                  _hover={{ transform: 'translateY(-3px)', boxShadow: '2xl', bg: 'gray.800' }}
                  _active={{ transform: 'translateY(0)' }}
                  transition="all 0.3s"
                  borderRadius="xl"
                >
                  <VStack spacing={0} align="flex-start">
                    <Text fontSize="xs" color="gray.300" fontWeight="normal" fontFamily="Tajawal, sans-serif">تحميل التحديث الجديد</Text>
                    <Text fontSize="lg" fontWeight="bold" fontFamily="Tajawal, sans-serif">تحميل الآن (Windows)</Text>
                  </VStack>
                </Button>

                <HStack spacing={4} w="100%" justify="center">
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    size="sm"
                    fontFamily="Tajawal, sans-serif"
                    onClick={() => window.location.reload()}
                  >
                    إعادة الفحص بعد التثبيت
                  </Button>

                  <Button
                    variant="ghost"
                    colorScheme="red"
                    size="sm"
                    fontFamily="Tajawal, sans-serif"
                    onClick={logout}
                  >
                    تسجيل الخروج
                  </Button>
                </HStack>
              </VStack>
            </VStack>

            {/* Right Mockup */}
            <Flex flex={1} justify="center" align="center" position="relative" w="100%" minH="380px">
              <Box
                position="absolute"
                w={{ base: "260px", md: "360px" }}
                h={{ base: "260px", md: "360px" }}
                bg="red.100"
                borderRadius="full"
                zIndex={0}
                filter="blur(20px)"
              />

              <MotionBox
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                zIndex={1}
                w="100%"
                maxW="380px"
              >
                <Box
                  bg="white"
                  borderRadius="2xl"
                  p={6}
                  boxShadow="2xl"
                  border="2px solid"
                  borderColor="red.200"
                  textAlign="center"
                >
                  <Box
                    w="90px"
                    h="90px"
                    bg="red.500"
                    color="white"
                    borderRadius="2xl"
                    mx="auto"
                    mb={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="lg"
                  >
                    <Icon as={FaShieldAlt} boxSize={12} />
                  </Box>
                  <Heading as="h4" size="md" fontFamily="Tajawal, sans-serif" mb={2} color="gray.800">
                    أمان وحماية الحصص
                  </Heading>
                  <Text fontSize="sm" color="gray.500" fontFamily="Tajawal, sans-serif" mb={4} lineHeight="1.6">
                    النسخة الجديدة تحتوي على تحديثات ضرورية للتحكم بالصوت وجودة الفيديو ومنع الأعطال.
                  </Text>
                  <Divider mb={4} />
                  <HStack justify="space-between" fontSize="xs" color="gray.500">
                    <Text>الإصدار الحالي: <b>قديم</b></Text>
                    <Text>الإصدار المطلوب: <b>1.1.0</b></Text>
                  </HStack>
                </Box>
              </MotionBox>
            </Flex>
          </Flex>

        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

export default DesktopUpdateGate;
